import { useLayoutEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth";
import { useCompanyTimezoneStore } from "@/stores/companyTimezone";
import { fetchPreferences } from "@/features/settings/api/settingsService";
import { validTimezone } from "@/lib/timezone";

/** Resolve the tenant's clock before mounting date pickers and default periods. */
export function CompanyTimezoneBoundary({ children }: { children: ReactNode }) {
  const company = useAuthStore((state) => state.user?.companyId);
  const companyId = company == null ? "" : String(company);
  const preferences = useQuery({
    queryKey: ["preferences", false, company],
    queryFn: () => fetchPreferences(),
    enabled: Boolean(companyId),
    retry: false,
    meta: { silentError: true },
  });
  const stored = useCompanyTimezoneStore();
  const timeZone =
    validTimezone(preferences.data?.companyPreference.timeZone) || "UTC";
  useLayoutEffect(() => {
    if (
      !preferences.isPending &&
      companyId &&
      (stored.companyId !== companyId || stored.timeZone !== timeZone)
    )
      stored.set(companyId, timeZone);
  }, [companyId, timeZone, stored, preferences.isPending]);
  if (companyId && stored.companyId !== companyId) {
    return (
      <p role="status" className="p-6 text-sm text-muted-foreground">
        Loading company preferences…
      </p>
    );
  }
  return children;
}
